import { useContext } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import {
  Col,
  Row,
  Dialog,
  Details,
  isMobile,
  Messages,
  mapQuery,
  useAlert,
  DialogType,
  ButtonTypes,
  ErrorMessage,
  ButtonColors,
  ThemeContext,
  StandardButton,
} from '@gv/triage-components';

import { useAppDispatch } from 'store';
import { cardsTag, clientsApi, useSetupIntentsQuery } from 'store/api/clients';

import * as Styles from './styles';
import { getCardStyles } from './config';
import { FormValues, CreateCardProps } from './types';

export const CreateCard = ({ onClose }: CreateCardProps) => {
  const mobile = isMobile();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const { theme } = useContext(ThemeContext);
  const { handleError, showSuccessAlert } = useAlert();

  const { data, ...props } = useSetupIntentsQuery();

  const onSubmit = async (
    values: FormValues,
    { setFieldError }: FormikHelpers<FormValues>
  ) => {
    try {
      if (!stripe || !elements || !data?.client_secret) {
        return;
      }

      const cardElement = elements.getElement(CardElement);

      const { error } = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: cardElement! },
      });

      if (error) {
        return setFieldError('isFormDirty', error.message);
      }

      showSuccessAlert(`Payment card ${Messages.ADDED_SUCCESSFULLY}`);
      dispatch(clientsApi.util.invalidateTags([cardsTag]));
      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Dialog
      open
      title="Add new card"
      type={DialogType.Plain}
      closeOnDocumentClick={false}
    >
      {(close, _, Buttons) => (
        <Formik<FormValues>
          onSubmit={onSubmit}
          initialValues={{ isFormDirty: undefined }}
        >
          {({
            dirty,
            errors,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Details {...mapQuery(props)}>
              <Styles.Form>
                <Row>
                  <Col>
                    <CardElement
                      options={{ style: getCardStyles(theme) }}
                      onChange={(event) => {
                        if (!event.empty) {
                          setFieldValue('isFormDirty', true);
                          setFieldTouched('isFormDirty', true);
                        }
                      }}
                    />
                    {errors.isFormDirty && (
                      <ErrorMessage>{errors.isFormDirty}</ErrorMessage>
                    )}
                  </Col>
                </Row>
                <Buttons type={DialogType.Plain}>
                  <StandardButton
                    text="Cancel"
                    onClick={onClose}
                    fullWidth={mobile}
                    colorType={ButtonColors.LightBlue}
                  />
                  <StandardButton
                    text="Create"
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    disabled={!dirty || !stripe || isSubmitting}
                  />
                </Buttons>
              </Styles.Form>
            </Details>
          )}
        </Formik>
      )}
    </Dialog>
  );
};
